import { useNavigate } from 'react-router-dom';
import { MenuItems } from 'src/app/AppRoutes';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSwitchAccount } from 'src/contexts/SwitchAccountContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useHasReleaseNotesUnread } from 'src/hooks/useHasReleaseNotesUnread';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import {
  DropdownDivider,
  DropdownLabel,
} from 'src/navigations/MainNav/MainNavTopBar/MainNavTopBar.styled';
import { UserInfoDisplay } from 'src/navigations/MainNav/UserInfoDisplay';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { UnreadDot } from 'src/navigations/Routes/ReleaseNotes';
import { ProfileOutlineIcon, SigninIcon, SignoutIcon } from 'src/svgs/Viagogo';
import { REACT_APP_REPORTS_BRIDGE_URL } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Permission } from 'src/WebApiController';

import { MenuItem } from '../MenuItem/MenuItem';
import {
  icon,
  menuItem,
  menuText,
  releaseNoteOptionContainer,
} from '../MenuItem/MenuItem.css';

const menuItems = [MainRoute.Settings] as const;

export const UserMenu = ({ toggleClose }: { toggleClose: () => void }) => {
  const { mainRoute } = useAppContext();
  return (
    <div>
      <Dropdown toggleClose={toggleClose} />
      {menuItems.map((item, index) => {
        const { view, path, label, icon } = MenuItems[item];
        return (
          <MenuItem
            key={index}
            isActive={mainRoute === view}
            textContent={label}
            icon={icon}
            path={path}
            onClick={toggleClose}
          />
        );
      })}
    </div>
  );
};

const MyAccountMenuItem = () => {
  const { loginContext } = useAppContext();
  const userDisplayName = loginContext?.user?.activeAccount.accountName;
  return (
    <>
      <div className={menuItem}>
        <div className={icon}>
          <ProfileOutlineIcon />
        </div>
        <span className={menuText}>
          {userDisplayName ?? <Content id={ContentId.MyAccount} />}
        </span>
      </div>
    </>
  );
};

const Dropdown = ({ toggleClose }: { toggleClose: () => void }) => {
  const { loginContext } = useAppContext();
  const signInText = useContent(ContentId.SignIn);
  const navigate = useNavigate();
  const { onSwitchAccountClicked } = useSwitchAccount();

  const myAccountText = useContent(ContentId.MyAccount);
  const menuText = loginContext ? myAccountText : signInText;

  const { hasReleaseNotesUnread } = useHasReleaseNotesUnread({});
  const canViewReleaseNotes = useUserHasAnyOfPermissions(
    Permission.General_ViewReleaseNotes
  );
  const canViewHelpCenter = useUserHasAnyOfPermissions(
    Permission.General_ViewHelpCenter
  );
  const canViewStubhubPayments = useUserHasAnyOfPermissions(
    Permission.Accounting_ViewStubhubPayments
  );

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer');
  };

  return (
    <>
      {loginContext ? (
        <PosDropdown
          trigger={<MyAccountMenuItem />}
          align="start"
          triggerProps={{ style: { width: '100%' } }}
        >
          <>
            <PosDropdownItem disabled style={{ color: vars.color.textPrimary }}>
              {loginContext ? (
                <UserInfoDisplay
                  userInfo={loginContext.user}
                  showUserName={false}
                />
              ) : (
                <DropdownLabel>{menuText}</DropdownLabel>
              )}
            </PosDropdownItem>
            <DropdownDivider />
            {loginContext?.user?.hasManyAccounts && (
              <PosDropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSwitchAccountClicked();
                  toggleClose();
                }}
              >
                <Content id={ContentId.SwitchAccount} />
              </PosDropdownItem>
            )}
            {canViewStubhubPayments && (
              <PosDropdownItem
                onClick={() => {
                  openInNewTab(REACT_APP_REPORTS_BRIDGE_URL!);
                  toggleClose();
                }}
              >
                <Content id={ContentId.Payments} />
              </PosDropdownItem>
            )}

            {canViewReleaseNotes && (
              <PosDropdownItem
                onClick={() => {
                  navigate('/release-notes');
                  toggleClose();
                }}
              >
                <div className={releaseNoteOptionContainer}>
                  <Content id={ContentId.WhatsNew} />
                  {hasReleaseNotesUnread ? <UnreadDot /> : null}
                </div>
              </PosDropdownItem>
            )}

            {canViewHelpCenter && (
              <PosDropdownItem
                onClick={() => {
                  navigate('/help');
                  toggleClose();
                }}
              >
                <div className={releaseNoteOptionContainer}>
                  <Content id={ContentId.Help} />
                </div>
              </PosDropdownItem>
            )}

            <DropdownDivider />
            <PosDropdownItem
              onClick={() => {
                navigate('/logout');
                toggleClose();
              }}
            >
              <Stack gap="m" alignItems="center">
                <SignoutIcon size={vars.iconSize.l} />
                <Content id={ContentId.SignOut} />
              </Stack>
            </PosDropdownItem>
          </>
        </PosDropdown>
      ) : (
        <MenuItem
          textContent={<Content id={ContentId.SignIn} />}
          path={'/login'}
          icon={SigninIcon}
          onClick={toggleClose}
        />
      )}
    </>
  );
};
